// Small styled building blocks shared by the redesigned pages. They mirror the
// designer's mock 1:1 (raw elements + inline styles) so the visuals match
// exactly and don't fight MUI's theme. Behaviour/logic lives in the pages.
import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { accentTint, color, font } from './tokens';
import { Icon, type IconName } from './icons';

// --- CopyButton -----------------------------------------------------------
// Copies to the clipboard and flips to a green "Copied" state for 1.4s. Two
// shapes: an icon-only square (dense rows) or a labelled pill (endpoints).
export const CopyButton = ({
  value,
  iconOnly = false,
  label = 'Copy',
}: {
  value: string;
  iconOnly?: boolean;
  label?: string;
}) => {
  const [copied, setCopied] = useState(false);
  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    void navigator.clipboard?.writeText(value).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
      },
      () => undefined,
    );
  };
  const shared: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: 6,
    border: `1px solid ${copied ? 'rgba(70,178,106,.5)' : color.borderInput}`,
    background: copied ? 'rgba(70,178,106,.12)' : color.surfaceChip,
    color: copied ? color.greenBright : color.textMuted,
  };
  if (iconOnly) {
    return (
      <button
        type="button"
        onClick={onClick}
        title="Copy"
        style={{ ...shared, width: 26, height: 26, flex: 'none' }}
      >
        <Icon name={copied ? 'check' : 'copy'} size={12} weight={copied ? 'bold' : 'regular'} />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ ...shared, gap: 5, height: 28, padding: '0 9px', fontSize: 11 }}
    >
      <Icon name={copied ? 'check' : 'copy'} size={13} weight={copied ? 'bold' : 'regular'} />
      {copied ? 'Copied' : label}
    </button>
  );
};

// --- Toggle ---------------------------------------------------------------
export const Toggle = ({ on }: { on: boolean }) => (
  // `display:inline-block` is essential: a bare <span> is inline and ignores
  // width/height, which collapses the track to zero and leaves the knob floating
  // at the edge. box-sizing keeps the 34×19 outer size regardless of the border.
  <span
    style={{
      display: 'inline-block',
      boxSizing: 'border-box',
      flex: 'none',
      width: 34,
      height: 19,
      borderRadius: 20,
      position: 'relative',
      verticalAlign: 'middle',
      transition: 'background .16s, border-color .16s',
      background: on ? color.accent : '#39414d',
      border: `1px solid ${on ? color.accent : 'rgba(255,255,255,.16)'}`,
    }}
  >
    <span
      style={{
        position: 'absolute',
        top: 1,
        left: 1,
        width: 15,
        height: 15,
        borderRadius: '50%',
        background: '#fff',
        boxShadow: '0 1px 2px rgba(0,0,0,.45)',
        transition: 'transform .16s',
        transform: `translateX(${on ? 15 : 0}px)`,
      }}
    />
  </span>
);

// --- Buttons --------------------------------------------------------------
type BtnVariant = 'primary' | 'ghost' | 'danger';
export const Btn = ({
  variant = 'ghost',
  icon,
  iconWeight,
  children,
  onClick,
  disabled,
  href,
  style,
  small,
}: {
  variant?: BtnVariant;
  icon?: IconName;
  iconWeight?: 'regular' | 'bold';
  children: ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  href?: string;
  style?: CSSProperties;
  small?: boolean;
}) => {
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    height: small ? 31 : 36,
    padding: small ? '0 12px' : '0 15px',
    borderRadius: 8,
    fontSize: small ? 12 : 12.5,
    fontWeight: variant === 'primary' ? 600 : 500,
    cursor: disabled ? 'default' : 'pointer',
    textDecoration: 'none',
    opacity: disabled ? 0.5 : 1,
    whiteSpace: 'nowrap',
  };
  const skin: CSSProperties =
    variant === 'primary'
      ? { background: color.accent, border: 'none', color: color.bg }
      : variant === 'danger'
      ? {
          background: 'none',
          border: `1px solid rgba(224,108,117,.3)`,
          color: color.red,
        }
      : {
          background: 'none',
          border: `1px solid ${color.borderStrong}`,
          color: color.textSecondary,
        };
  const props = {
    style: { ...base, ...skin, ...style },
    onClick: disabled ? undefined : onClick,
  };
  if (href && !disabled) {
    return (
      <a {...props} href={href} target="_blank" rel="noreferrer">
        {icon && <Icon name={icon} size={small ? 12 : 14} weight={iconWeight} />}
        {children}
      </a>
    );
  }
  return (
    <button type="button" {...props} disabled={disabled}>
      {icon && <Icon name={icon} size={small ? 12 : 14} weight={iconWeight} />}
      {children}
    </button>
  );
};

// --- Text field / select --------------------------------------------------
const controlStyle = (mono: boolean, invalid?: boolean): CSSProperties => ({
  width: '100%',
  background: color.surfaceInset,
  border: `1px solid ${invalid ? 'rgba(224,108,117,.55)' : color.borderInput}`,
  borderRadius: 9,
  padding: '9px 12px',
  color: color.textBright,
  fontSize: mono ? 13.5 : 13,
  fontFamily: mono ? font.mono : font.sans,
});

export const TextInput = ({
  value,
  onChange,
  placeholder,
  mono = false,
  readOnly = false,
  invalid = false,
  dashed = false,
  list,
  style,
}: {
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
  readOnly?: boolean;
  invalid?: boolean;
  dashed?: boolean;
  list?: string;
  style?: CSSProperties;
}) => (
  <input
    value={value}
    list={list}
    readOnly={readOnly}
    placeholder={placeholder}
    spellCheck={false}
    onChange={onChange ? e => onChange(e.target.value) : undefined}
    style={{
      ...controlStyle(mono, invalid),
      ...(readOnly
        ? {
            color: color.textDim,
            border: dashed
              ? `1px dashed ${color.borderStrong}`
              : `1px solid ${color.borderSoft}`,
          }
        : {}),
      ...style,
    }}
  />
);

export const Select = ({
  value,
  onChange,
  children,
  icon = 'caret-down',
}: {
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
  icon?: IconName;
}) => (
  <div style={{ position: 'relative' }}>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        ...controlStyle(false),
        padding: '9px 34px 9px 12px',
        cursor: 'pointer',
      }}
    >
      {children}
    </select>
    <span
      style={{
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        display: 'flex',
      }}
    >
      <Icon name={icon} size={12} color={color.textDimmer} />
    </span>
  </div>
);

export const FieldLabel = ({
  children,
  lock = false,
}: {
  children: ReactNode;
  lock?: boolean;
}) => (
  <label
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 11.5,
      color: color.textFaint,
      marginBottom: 7,
    }}
  >
    {children}
    {lock && <Icon name="lock" size={12} color={color.textPlaceholder} />}
  </label>
);

export const Hint = ({
  children,
  mono = false,
}: {
  children: ReactNode;
  mono?: boolean;
}) => (
  <div
    style={{
      fontSize: 10.5,
      color: color.textDimmest,
      marginTop: 6,
      fontFamily: mono ? font.mono : font.sans,
    }}
  >
    {children}
  </div>
);

// --- Overline / section eyebrow ------------------------------------------
export const Eyebrow = ({ children }: { children: ReactNode }) => (
  <span
    style={{
      font: `600 11px ${font.mono}`,
      letterSpacing: '.12em',
      color: color.textDim,
    }}
  >
    {children}
  </span>
);

// --- Card (section container with optional header) -----------------------
export const Card = ({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) => (
  <section
    style={{
      background: color.surface,
      border: `1px solid ${color.border}`,
      borderRadius: 14,
      overflow: 'hidden',
      ...style,
    }}
  >
    {children}
  </section>
);

export const CardHeader = ({ children }: { children: ReactNode }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '14px 18px',
      borderBottom: `1px solid ${color.borderSoft}`,
    }}
  >
    {children}
  </div>
);

// --- Confirm dialog -------------------------------------------------------
// Modal used for irreversible actions (destroy). Rendered through a portal on
// <body> so it escapes the card's `overflow:hidden` and the clickable row —
// clicks inside it never bubble back into the list item. Esc cancels, the
// backdrop cancels, and the page behind it is scroll-locked while open.
export const ConfirmDialog = ({
  open,
  title,
  icon = 'warning',
  tone = 'danger',
  confirmLabel = 'Confirm',
  confirmIcon = 'trash',
  cancelLabel = 'Cancel',
  busy = false,
  onConfirm,
  onCancel,
  children,
}: {
  open: boolean;
  title: string;
  icon?: IconName;
  tone?: 'danger' | 'accent';
  confirmLabel?: string;
  confirmIcon?: IconName;
  cancelLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children: ReactNode;
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onCancel]);

  if (!open) return null;
  const accent = tone === 'danger' ? color.red : color.accent;

  return createPortal(
    // Keyboard users dismiss with Esc (handled above) or the Cancel button, so
    // the backdrop only needs the pointer affordance.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      className="ntt-scope ntt-modal-backdrop"
      role="presentation"
      onClick={e => {
        e.stopPropagation();
        // Only a click on the scrim itself dismisses; clicks inside the card
        // bubble up here but must not close it.
        if (e.target === e.currentTarget) onCancel();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        background: 'rgba(5,6,9,.72)',
        backdropFilter: 'blur(3px)',
        fontFamily: font.sans,
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="ntt-modal-card"
        style={{
          width: 'min(460px, 100%)',
          background: color.surface,
          border: `1px solid ${color.border}`,
          borderRadius: 14,
          boxShadow: '0 24px 60px rgba(0,0,0,.6)',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', gap: 13, padding: '20px 22px 0' }}>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
              width: 34,
              height: 34,
              borderRadius: 9,
              background:
                tone === 'danger' ? 'rgba(224,108,117,.14)' : accentTint(14),
              border: `1px solid ${
                tone === 'danger' ? 'rgba(224,108,117,.32)' : accentTint(34)
              }`,
              color: accent,
            }}
          >
            <Icon name={icon} size={17} weight="bold" color={accent} />
          </span>
          <div style={{ minWidth: 0, paddingTop: 4 }}>
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: color.textStrong,
              }}
            >
              {title}
            </h2>
          </div>
        </div>

        <div
          style={{
            padding: '12px 22px 20px',
            fontSize: 12.5,
            lineHeight: 1.6,
            color: color.textMuted,
          }}
        >
          {children}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 9,
            padding: '14px 22px',
            borderTop: `1px solid ${color.borderSoft}`,
            background: color.surfaceInset,
          }}
        >
          <Btn onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Btn>
          <Btn
            variant={tone === 'danger' ? 'danger' : 'primary'}
            icon={confirmIcon}
            disabled={busy}
            onClick={onConfirm}
            style={
              tone === 'danger'
                ? { background: color.red, border: 'none', color: color.bg, fontWeight: 600 }
                : undefined
            }
          >
            {confirmLabel}
          </Btn>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// --- Tabs -----------------------------------------------------------------
export type TabDef = { id: string; label: string; icon: IconName; count?: string };
export const Tabs = ({
  tabs,
  active,
  onSelect,
}: {
  tabs: TabDef[];
  active: string;
  onSelect: (id: string) => void;
}) => (
  <div
    style={{
      display: 'flex',
      gap: 2,
      padding: '11px 18px 0',
      borderBottom: `1px solid ${color.borderSoft}`,
    }}
  >
    {tabs.map(t => {
      const on = active === t.id;
      return (
        <button
          key={t.id}
          type="button"
          onClick={() => onSelect(t.id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '9px 13px',
            background: 'none',
            border: 'none',
            borderBottom: `2px solid ${on ? color.accent : 'transparent'}`,
            color: on ? color.textBright : color.textDim,
            fontSize: 12.5,
            fontWeight: on ? 600 : 500,
            cursor: 'pointer',
            marginBottom: -1,
          }}
        >
          <Icon name={t.icon} size={14} />
          {t.label}
          {t.count ? (
            <span
              style={{
                fontSize: 10,
                background: '#1a1e26',
                color: color.textFaint,
                padding: '0 5px',
                borderRadius: 4,
              }}
            >
              {t.count}
            </span>
          ) : null}
        </button>
      );
    })}
  </div>
);

// --- Meta grid tile (AWS account / Region / ...) --------------------------
export const MetaTile = ({ k, v }: { k: string; v: ReactNode }) => (
  <div
    style={{
      background: color.surface,
      border: `1px solid ${color.borderSoft}`,
      borderRadius: 9,
      padding: '11px 13px',
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontSize: 10,
        color: color.textDimmer,
        textTransform: 'uppercase',
        letterSpacing: '.06em',
        marginBottom: 5,
      }}
    >
      {k}
    </div>
    <div
      style={{
        font: `500 12.5px ${font.mono}`,
        color: color.textSecondary,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {v}
    </div>
  </div>
);

// --- Small accent-tinted "counter" used in page headers -------------------
export const StatCounter = ({
  value,
  label,
  tone = color.text,
}: {
  value: ReactNode;
  label: string;
  tone?: string;
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
    <span style={{ font: `600 20px ${font.mono}`, color: tone, lineHeight: 1 }}>
      {value}
    </span>
    <span
      style={{
        fontSize: 10.5,
        color: color.textDimmer,
        letterSpacing: '.04em',
        marginTop: 3,
      }}
    >
      {label}
    </span>
  </div>
);

export { color, font, accentTint };
export const useMemoStyle = <T,>(fn: () => T, deps: unknown[]) =>
  // Tiny helper so callers can memoise inline style objects without importing
  // useMemo everywhere.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(fn, deps);
